import StickyPrompt from 'components/Common/StickyPromptCard/StickyPrompt'
import { rem } from 'polished'
import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { matchPath } from 'react-router-dom'
import styled from 'styled-components'
import AccountAccessPromptIcon from './AccountAccessPromptIcon'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal from '../AccountAccess/AccountAccessModal'
import Group from 'components/utils/Group'
import IconButton from 'LuxKit/Button/IconButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LineGlassMartiniAltIcon from 'LuxKit/Icons/line/LineGlassMartiniAltIcon'
import LineGlobeAltIcon from 'LuxKit/Icons/line/LineGlobeAltIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import useClickOutside from 'hooks/useClickOutside'
import { useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import config from 'constants/config'
import { skipLoginModal } from 'components/Account/AccountAccessPrompt/AccountAccessPromptUtils'

const AccessWrapper = styled.div`
  position: relative;
`

const CloseButton = styled(IconButton)`
  position: absolute;
  right: ${rem(20)};
  top: ${rem(20)};
`

const AccessPrompt = styled(StickyPrompt)`
  padding: ${rem(24)};

  ${mediaQueryUp.tablet} {
    width: ${rem(400)};
  }
`

const AccountAccessPromptRoutes = [
  '/:regionCode/hotels',
  '/:regionCode/tours',
  '/:regionCode/cruises',
  '/:regionCode/car-hire',
  '/:regionCode/experiences',
  '/:regionCode/flights',
  '/:regionCode/lux-plus',
  '/:regionCode/ultra-lux',
]

const SearchPageAccountAccessPromptRoute = '/:regionCode/search'

/**
 * We only want to show the prompt once per 'session' for the user
 * We could save this in redux, but that feels way overkill. Lets just have some local state
 */
let hasSeenPrompt = false

function AccountAccessPrompt() {
  const intl = useIntl()
  const promptRef = useRef<HTMLDivElement>(null)
  const [show, setShow] = useState<boolean>(false)
  const showModal = useContext(ModalContext)
  const loggedIn = useAppSelector(selectLoggedIn)
  const currentPath = useAppSelector(state => state.router.location.pathname)
  const utmContent = useAppSelector(state => state.utm.content)
  const utmBp = useAppSelector(state => state.utm.bp)
  const isMobileDevice = useIsMobileDevice()

  const showSearchPageLoginModal = !skipLoginModal(utmBp, utmContent)

  const canShow = useMemo(() => {
    // homepage needs an exact match (otherwise it matches every path!)
    return config.ENABLE_ACCOUNT_ACCESS_PROMPT &&
      (
        matchPath({ path: '/:regionCode', end: true }, currentPath) ||
        // all other pages work with unexact
        AccountAccessPromptRoutes.some(path => matchPath({ path, end: false }, currentPath)) ||

        (matchPath({ path: SearchPageAccountAccessPromptRoute, end: false }, currentPath) && showSearchPageLoginModal)
      )
  }, [currentPath, showSearchPageLoginModal])

  useEffect(() => {
    if (!loggedIn && !hasSeenPrompt) {
      setShow(true)
      hasSeenPrompt = true
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (loggedIn || !canShow) {
      setShow(false)
    }
  }, [loggedIn, canShow])

  const dismissPrompt = useCallback(() => {
    setShow(false)
  }, [])

  const onLogin = useCallback(() => {
    dismissPrompt()
    showModal(<AccountAccessModal />)
  }, [dismissPrompt, showModal])

  useClickOutside(promptRef, dismissPrompt, show)

  if (isMobileDevice) {
    return null
  }

  const moreInclusionsText = intl.formatMessage({
    id: 'vT8Ooy2SQXKMC69jK0WF',
    defaultMessage: 'Drinks, dining, transfers & more',
    description: 'Benefit description for inclusions in account access prompt',
  })

  const moreOptionsText = intl.formatMessage({
    id: '0x6-AxfqzmCMaNScygaA',
    defaultMessage: '1,000s of hotels, tours & flights',
    description: 'Benefit description for travel options in account access prompt',
  })

  const moreExpertiseText = intl.formatMessage({
    id: 'ccMqzb6EtNcNP4SE1Q7I',
    defaultMessage: '24/7 customer service, worldwide',
    description: 'Benefit description for customer service in account access prompt',
  })

  const moreInclusionsLabel = intl.formatMessage({
    id: '4LhcJCRToMipDnYhGu0_',
    defaultMessage: 'More inclusions:',
    description: 'Bold label for inclusions benefit in account access prompt',
  })

  const moreOptionsLabel = intl.formatMessage({
    id: 'OW78U-0ZOYXXfPiMidqO',
    defaultMessage: 'More options:',
    description: 'Bold label for options benefit in account access prompt',
  })

  const moreExpertiseLabel = intl.formatMessage({
    id: 'mTQy6c6rqWYnkNZwWFQm',
    defaultMessage: 'More expertise:',
    description: 'Bold label for expertise benefit in account access prompt',
  })

  const loginButtonLabel = intl.formatMessage({
    id: 'pvgVh5FmYNoYdDue9fkK',
    defaultMessage: 'Log in or sign up',
    description: 'CTA button text on account access prompt in account settings',
  })

  return <AccessWrapper>
    <AccessPrompt show={show} sticky={false} ref={promptRef}>
      <CloseButton
        kind="tertiary"
        shape="square"
        onClick={dismissPrompt}
      >
        <LineTimesIcon />
      </CloseButton>
      <Group direction="vertical" horizontalAlign="center" gap={16}>
        <AccountAccessPromptIcon />
        <VerticalSpacer gap={24}>
          <VerticalSpacer gap={12}>
            <Heading variant="heading3">
              <FormattedMessage
                id="vEQe8nS2tTfKO8bEd7zz"
                defaultMessage="Exclusive prices and <i>more</i> with every booking"
                description="Heading text on account access prompt with italicised more"
                values={{
                  i: (chunks) => <i>{chunks}</i>,
                }}
              />
            </Heading>
            <VerticalSpacer gap={4}>
              <BodyTextBlock variant="medium" startIcon={<LineGlassMartiniAltIcon colour="highlight-primary-dark" />}>
                <b>{moreInclusionsLabel}</b> {moreInclusionsText}
              </BodyTextBlock>
              <BodyTextBlock variant="medium" startIcon={<LineGlobeAltIcon colour="highlight-primary-dark" />}>
                <b>{moreOptionsLabel}</b> {moreOptionsText}
              </BodyTextBlock>
              <BodyTextBlock variant="medium" startIcon={<LinePhoneIcon colour="highlight-primary-dark" />}>
                <b>{moreExpertiseLabel}</b> {moreExpertiseText}
              </BodyTextBlock>
            </VerticalSpacer>
          </VerticalSpacer>
          <TextButton size="large" kind="primary" fit="full-width" onClick={onLogin}>
            {loginButtonLabel}
          </TextButton>
        </VerticalSpacer>
      </Group>
    </AccessPrompt>
  </AccessWrapper>
}

export default memo(AccountAccessPrompt)
